import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { AccountsPayableService } from '../../../services/accounts-payable.service';
import { AccountsReceivableService } from '../../../services/accounts-receivable.service';
import { ContractsService } from '../../../services/contracts.service';
import { AccountsPayableDepositRefundCreate } from '../../../models/AccountsPayable';
import { AccountsReceivableCreate } from '../../../models/AccountsReceivable';
import { ErrorService } from '../../../shared/services/error.service';

export type DepositRefundMode = 'b2c' | 'b2b';

/** Estado opcional ao abrir a partir da listagem de contas a pagar. */
export interface AccountsPayableListReturnState {
  page?: number;
  pageSize?: number;
}

@Component({
  selector: 'app-deposit-refund-accounts-payable',
  templateUrl: './deposit-refund-accounts-payable.component.html',
  styleUrls: ['./deposit-refund-accounts-payable.component.css']
})
export class DepositRefundAccountsPayableComponent implements OnInit {
  mode: DepositRefundMode = 'b2c';
  contractId!: number;
  form!: FormGroup;
  loadingContext = false;
  isSubmitting = false;
  error: string | null = null;

  contractLabel = '';
  contractNumber = '';
  contractCustomerId?: number;
  suggestedDepositAmount: number | null = null;

  /** Tipo em Contas a Receber e Contas a Pagar (não usar "Extorno"). */
  readonly documentTypeLabel = 'Devolução Caução';
  readonly statusFixed = 'Pendente';
  private readonly dueDaysFromIssue = 90;

  private accountsPayableReturn: AccountsPayableListReturnState | null = null;
  private returnToReceivableList = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private accountsPayableService: AccountsPayableService,
    private accountsReceivableService: AccountsReceivableService,
    private contractsService: ContractsService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.mode = (this.route.snapshot.data['depositRefundMode'] as DepositRefundMode) || 'b2c';
    this.contractId = +this.route.snapshot.params['contractId'];
    const navState = this.router.getCurrentNavigation()?.extras?.state as
      | { accountsPayableReturn?: AccountsPayableListReturnState; returnToReceivableList?: boolean }
      | undefined;
    this.accountsPayableReturn =
      navState?.accountsPayableReturn ?? history.state?.accountsPayableReturn ?? null;
    this.returnToReceivableList =
      navState?.returnToReceivableList ?? history.state?.returnToReceivableList ?? false;
    this.buildForm();
    this.loadContractContext();
  }

  get isB2b(): boolean {
    return this.mode === 'b2b';
  }

  get pageTitle(): string {
    return this.isB2b ? 'Devolução de caução — B2B' : 'Devolução de caução — B2C';
  }

  private buildForm(): void {
    const today = this.todayIso();
    const year = new Date().getFullYear();
    const docPrefix = this.isB2b ? 'DEV-B2B' : 'DEV';
    const docDefault = `${docPrefix}-${year}-${String(this.contractId).padStart(3, '0')}`;

    this.form = this.fb.group({
      documentNumber: [docDefault, [Validators.required, Validators.maxLength(50)]],
      documentType: [this.documentTypeLabel],
      issueDate: [today, [Validators.required]],
      dueDate: [this.dueDateFromIssue(today), [Validators.required]],
      amount: [null, [Validators.required, Validators.min(0.01)]],
      description: [
        this.isB2b ? 'Devolução caução contrato B2B' : `Devolução caução contrato ${this.contractId}`,
        this.isB2b ? [Validators.required, Validators.maxLength(500)] : [Validators.maxLength(500)]
      ]
    });
  }

  private dueDateFromIssue(issueDate: string): string {
    return this.addDaysIso(issueDate, this.dueDaysFromIssue);
  }

  private loadContractContext(): void {
    this.loadingContext = true;
    if (this.isB2b) {
      this.contractsService.getB2bContractById(this.contractId).subscribe({
        next: (c) => {
          this.contractLabel = c.contractName || c.companyName || 'Contrato B2B';
          this.contractNumber = c.contractNumber || '';
          this.contractCustomerId = c.customerId;
          const dep = Number(c.depositAmount) || 0;
          if (dep > 0) {
            this.suggestedDepositAmount = dep;
            this.patchSuggestedAmount(dep);
          }
          this.loadingContext = false;
        },
        error: () => {
          this.loadingContext = false;
          this.error = 'Não foi possível carregar os dados do contrato B2B.';
        }
      });
      return;
    }

    this.contractsService.getContractComplete(this.contractId).subscribe({
      next: (data) => {
        const c = data?.contract || data;
        this.contractLabel = c.contractNumber ? `Contrato #${c.contractNumber}` : 'Contrato B2C';
        this.contractNumber = c.contractNumber || String(c.id);
        this.contractCustomerId = c.customerId;
        const dep = Number(c.depositAmount) || 0;
        if (dep > 0) {
          this.suggestedDepositAmount = dep;
          this.patchSuggestedAmount(dep);
        }
        this.loadingContext = false;
      },
      error: () => {
        this.loadingContext = false;
        this.error = 'Não foi possível carregar os dados do contrato B2C.';
      }
    });
  }

  private patchSuggestedAmount(amount: number): void {
    if (this.form.get('amount')?.value == null) {
      this.form.patchValue({ amount });
    }
  }

  applySuggestedAmount(): void {
    if (this.suggestedDepositAmount != null && this.suggestedDepositAmount > 0) {
      this.form.patchValue({ amount: this.suggestedDepositAmount });
    }
  }

  private toAmountValue(value: unknown): number {
    if (value === null || value === undefined || value === '') return 0;
    const num = Number(value);
    return Number.isNaN(num) ? 0 : num;
  }

  private buildAccountsReceivablePayload(
    amount: number,
    issueDate: string,
    documentNumber: string
  ): AccountsReceivableCreate {
    const desc = String(this.form.get('description')?.value || '').trim();
    return {
      documentNumber: `CR-${documentNumber}`,
      documentType: this.documentTypeLabel,
      issueDate,
      dueDate: this.form.get('dueDate')?.value,
      originalAmount: amount,
      amountToReceive: amount,
      status: this.statusFixed,
      category: 'Caução',
      customerId: this.contractCustomerId,
      contractNumber: this.contractNumber,
      saleOrderNumber: `CONTRATO-${this.contractId}`,
      description: desc || `Devolução de caução — contrato ${this.contractNumber || this.contractId}`
    };
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.isSubmitting) return;

    const amount = this.toAmountValue(this.form.get('amount')?.value);
    if (amount < 0.01) {
      this.error = 'Informe um valor da devolução maior que zero.';
      return;
    }

    if (!this.contractCustomerId) {
      this.error = 'Contrato sem cliente vinculado — não é possível registrar em Contas a Receber.';
      return;
    }

    const issueDate = this.todayIso();
    const documentNumber = String(this.form.get('documentNumber')?.value || '').trim();
    const documentType = String(this.form.get('documentType')?.value || this.documentTypeLabel).trim();

    const apPayload: AccountsPayableDepositRefundCreate = {
      documentNumber,
      documentType,
      issueDate,
      dueDate: this.form.get('dueDate')?.value,
      originalAmount: amount,
      amountToPay: amount,
      status: this.statusFixed
    };

    if (this.isB2b) {
      apPayload.b2BContractId = this.contractId;
      apPayload.isDepositRefund = true;
      apPayload.description = String(this.form.get('description')?.value || '').trim();
    } else {
      apPayload.contractDraftId = this.contractId;
      const desc = String(this.form.get('description')?.value || '').trim();
      if (desc) {
        apPayload.description = desc;
      }
    }

    const arPayload = this.buildAccountsReceivablePayload(amount, issueDate, documentNumber);

    this.isSubmitting = true;
    this.error = null;

    this.accountsReceivableService
      .createAccountsReceivable(arPayload)
      .pipe(switchMap(() => this.accountsPayableService.createDepositRefund(apPayload)))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.errorService.showInfoMessage(
            'Devolução de caução registrada em Contas a Receber e Contas a Pagar.'
          );
          this.navigateAfterSuccess();
        },
        error: (err) => {
          this.isSubmitting = false;
          this.error = err?.error?.message || err?.message || 'Erro ao registrar devolução de caução.';
        }
      });
  }

  cancel(): void {
    if (this.accountsPayableReturn) {
      this.navigateBackToAccountsPayableList();
      return;
    }
    if (this.returnToReceivableList) {
      this.router.navigate(['/financial/accounts-receivable']);
      return;
    }
    if (this.isB2b) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    } else {
      this.router.navigate(['/contracts', this.contractId, 'view']);
    }
  }

  private navigateAfterSuccess(): void {
    if (this.returnToReceivableList) {
      this.router.navigate(['/financial/accounts-receivable']);
      return;
    }
    if (this.accountsPayableReturn) {
      this.navigateBackToAccountsPayableList();
      return;
    }
    this.router.navigate(['/financial/accounts-receivable']);
  }

  private navigateBackToAccountsPayableList(): void {
    const queryParams: Record<string, number> = {};
    const page = this.accountsPayableReturn?.page;
    const pageSize = this.accountsPayableReturn?.pageSize;
    if (page != null && page > 0) {
      queryParams['page'] = page;
    }
    if (pageSize != null && pageSize > 0) {
      queryParams['pageSize'] = pageSize;
    }
    this.router.navigate(['/financial/accounts-payable'], {
      queryParams: Object.keys(queryParams).length ? queryParams : undefined
    });
  }

  isFieldInvalid(name: string): boolean {
    const c = this.form.get(name);
    return !!(c && c.invalid && (c.dirty || c.touched));
  }

  private todayIso(): string {
    return new Date().toISOString().split('T')[0];
  }

  private addDaysIso(isoDate: string, days: number): string {
    const d = new Date(isoDate + 'T12:00:00');
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  }
}
